import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  intlShape
} from 'react-intl';
import { get } from 'lodash';
import RouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';
import { Alert } from 'reactstrap';
import PropTypes from 'prop-types';

import { modelOf } from '../../../prop-types';
import PageTitle from '../../common/PageTitle';
import SEOTitle from '../../head/SEOTitle';
import ContentForState from '../../loader/ContentForState';
import Paths from '../../../types/Paths';
import UnexpectedError from '../../loader/UnexpectedError';
import globalTranslations from '../../../i18n/globalTranslations';
import RouteService from '../../../services/RouteService';
import HelperNavigation from '../../common/HelperNavigation';
import generatePath from '../../../util/generatePath';
import CustomerUserStore from '../../../store/CustomerUserStore';
import UserForm from '../../user/UserForm';
import AccountStore from '../../../store/AccountStore';
import BalanceUserForm from '../../user/BalanceUserForm';

const messages = defineMessages({
  updateUser: {
    id: 'myAccount.updateUser',
    defaultMessage: 'Update User'
  }
});

@observer
export class MyAccountUser extends Component {
  state = {
    updateError: null,
    requestId: 0
  };

  componentDidMount() {
    this.loadUserAccountType();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;

    if (prevProps.match.params.id !== match.params.id) {
      this.loadUserAccountType();
    }
  }

  loadUserAccountType = () => {
    const { accountStore } = this.props;
    if (accountStore.isBalanceBuyer) {
      this.maybeLoadBalanceUser();
    } else {
      this.maybeLoadUser();
    }
  };

  maybeLoadUser = () => {
    const { customerUserStore } = this.props;
    const user = this.getActiveUser();
    if (!user) {
      customerUserStore
        .loadUser(this.getActiveUserId())
        .catch((e) => console.error(e));
    }
  };

  maybeLoadBalanceUser = () => {
    const { customerUserStore } = this.props;
    const user = this.getActiveUser();
    if (!user) {
      customerUserStore
        .loadBalanceUser(this.getActiveUserId())
        .catch((e) => console.error(e));
    }
  };

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccount
      },
      {
        text: intl.formatMessage(globalTranslations.userListTitle),
        url: Paths.MyAccountUserList
      },
      {
        text: intl.formatMessage(messages.updateUser),
        url: generatePath(Paths.MyAccountUser, {
          id: this.getActiveUserId()
        })
      }
    ]);
  };

  getActiveUserId = () => {
    const { match } = this.props;
    return match.params.id;
  };

  getActiveUser = () => {
    const { customerUserStore, accountStore } = this.props;
    return accountStore.isBalanceBuyer
      ? customerUserStore.balanceUsers.get(this.getActiveUserId())
      : customerUserStore.users.get(this.getActiveUserId());
  };

  updateCustomerUser = (user) => {
    const { customerUserStore } = this.props;

    user.isActive = !!user.isActive;
    user.notifyUser = !!user.notifyUser;
    user.allowUserCreateAddress = !!user.allowUserCreateAddress;

    customerUserStore
      .updateUser(this.getActiveUserId(), user)
      .then(() => {
        this.setState({ updateError: null });
        customerUserStore.loadUsers();
        this.navigateToUserList();
      })
      .catch((error) =>
        this.setState({
          updateError: error,
          requestId: this.state.requestId + 1
        })
      );
  };

  navigateToUserList() {
    const { history, routeService } = this.props;
    history.push(routeService.getPath(Paths.MyAccountUserList));
  }

  updateBalanceUser = (user) => {
    const { customerUserStore } = this.props;
    user.isActive = !!user.isActive;
    user.notifyUser = !!user.notifyUser;

    customerUserStore
      .updateBalanceUser(this.getActiveUserId(), user)
      .then(() => {
        this.setState({ updateError: null });
        customerUserStore.loadBalanceUsers();
        this.navigateToBalanceUserList();
      })
      .catch((error) =>
        this.setState({
          updateError: error,
          requestId: this.state.requestId + 1
        })
      );
  };

  navigateToBalanceUserList() {
    const { history, routeService } = this.props;
    history.push(routeService.getPath(Paths.MyAccountBalanceUser));
  }

  renderUserForm = () => {
    const { accountStore } = this.props;

    return accountStore.isBalanceBuyer ? (
      <BalanceUserForm
        user={this.getActiveUser()}
        onSubmit={this.updateBalanceUser}
        buttonText={<FormattedMessage {...globalTranslations.update} />}
      />
    ) : (
      <UserForm
        user={this.getActiveUser()}
        onSubmit={this.updateCustomerUser}
        buttonText={<FormattedMessage {...globalTranslations.update} />}
      />
    );
  };

  renderError = () => {
    const { updateError, requestId } = this.state;
    const errors = get(updateError, 'response.data');
    return errors ? (
      <Alert className="MyAccountUser__errors" color="danger" key={requestId}>
        {Object.values(updateError.response.data).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  render() {
    const { customerUserStore, accountStore } = this.props;
    const { updateError } = this.state;
    const accountTypeState = accountStore.isBalanceBuyer
      ? customerUserStore.balanceUserStates.get(this.getActiveUserId())
      : customerUserStore.userStates.get(this.getActiveUserId());
    return (
      <div className="MyAccountUser">
        <SEOTitle title={messages.updateUser} />
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <PageTitle>
          <FormattedMessage {...messages.updateUser} />
        </PageTitle>
        {updateError && this.renderError()}
        <ContentForState
          state={accountTypeState}
          error={customerUserStore.lastError}
          forLoaded={() => this.renderUserForm()}
        />
      </div>
    );
  }
}

MyAccountUser.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  history: RouterPropTypes.history.isRequired,
  intl: intlShape.isRequired,
  match: RouterPropTypes.match.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired
};

export default injectIntl(
  inject('customerUserStore', 'routeService', 'accountStore')(
    withRouter(MyAccountUser)
  )
);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountUser/index.jsx